/**
 * Product modules: the sellable parts of the platform. Mirrors the API's
 * ProductModule enum and its permission-prefix map (ProductModuleService), so
 * the app can grey out navigation for modules a tenant's plan does not include
 * before the API says no.
 */
export type ProductModule =
  | 'Signup'
  | 'Installs'
  | 'Support'
  | 'Construction'
  | 'Network'
  | 'Billing'
  | 'CustomerPortal';

export const ALL_MODULES: ProductModule[] = [
  'Signup',
  'Installs',
  'Support',
  'Construction',
  'Network',
  'Billing',
  'CustomerPortal',
];

export const MODULE_LABELS: Record<ProductModule, string> = {
  Signup: 'Signup & website',
  Installs: 'Installs & Dispatch',
  Support: 'Support',
  Construction: 'Construction',
  Network: 'Network & fiber',
  Billing: 'Billing',
  CustomerPortal: 'Customer portal',
};

export const MODULE_DESCRIPTIONS: Record<ProductModule, string> = {
  Signup:
    'The address check and signup form, the hosted marketing site and embed snippet, the Signup Funnel report, waitlists and coming-soon dates, qualification lists, promo codes and install deposits.',
  Installs:
    'Install scheduling, the dispatch board, the Dispatch field app, provisioning, drop bury, equipment and fleet vehicles.',
  Support: 'Support tickets, outages and outage notifications.',
  Construction: '811 locate tickets and One Call integrations, construction milestones, permits and contractors.',
  Network: 'Network sites, fiber routes and enclosures, KMZ import and the network map.',
  Billing: 'Invoices, payments, autopay and payment processing.',
  CustomerPortal: 'Customer login, invoices and payments online, plan and install details.',
};

/** The cheapest bundle that includes each module, or the add-on that adds it. */
export const MODULE_AVAILABILITY: Record<ProductModule, string> = {
  Signup: 'Included in every bundle',
  Installs: 'Included in Operations',
  Support: 'Included in Operations',
  Construction: 'Included in Operations',
  Network: 'Included in Operations',
  Billing: 'Included in Complete, or as an add-on to Operations',
  CustomerPortal: 'Included in Complete, or as an add-on to Operations',
};

/**
 * Permission-key prefix to owning module. Keep in step with
 * ProductModuleService.PermissionPrefixModules on the API. Anything missing is
 * core and always available.
 */
const PERMISSION_PREFIX_MODULES: Record<string, ProductModule> = {
  'qualification-lists': 'Signup',
  'promo-codes': 'Signup',
  campaigns: 'Signup',
  nurturing: 'Signup',
  winback: 'Signup',
  canvass: 'Signup',
  surveys: 'Signup',
  serviceability_protection: 'Signup',
  installations: 'Installs',
  scheduling: 'Installs',
  dispatch: 'Installs',
  technician: 'Installs',
  technicians: 'Installs',
  availability: 'Installs',
  pto: 'Installs',
  drop_bury: 'Installs',
  equipment: 'Installs',
  fleet: 'Installs',
  materials: 'Installs',
  adtran: 'Installs',
  calix: 'Installs',
  uisp: 'Installs',
  tickets: 'Support',
  outages: 'Support',
  escalation: 'Support',
  locate_tickets: 'Construction',
  'locate-tickets': 'Construction',
  onecall_settings: 'Construction',
  permits: 'Construction',
  pole_attachments: 'Construction',
  'site-plans': 'Construction',
  contractors: 'Construction',
  'network-sites': 'Network',
  fiber: 'Network',
  parcels: 'Network',
  billing: 'Billing',
  billing_settings: 'Billing',
  invoices: 'Billing',
  payments: 'Billing',
  payment_methods: 'Billing',
  autopay: 'Billing',
  credits: 'Billing',
  bill_runs: 'Billing',
  tax_rates: 'Billing',
};

export function moduleForPermission(permission: string | undefined): ProductModule | null {
  if (!permission) return null;
  const dot = permission.indexOf('.');
  const prefix = dot > 0 ? permission.slice(0, dot) : permission;
  return PERMISSION_PREFIX_MODULES[prefix] ?? null;
}

/** Dashboard widgets that belong to a module (others are core). */
export const WIDGET_MODULES: Record<string, ProductModule> = {
  'support-tickets': 'Support',
  'my-assignments': 'Installs',
  'upcoming-installations': 'Installs',
  'pending-workflow': 'Installs',
  'pending-activations': 'Installs',
  permits: 'Construction',
};

export function isProductModule(value: string): value is ProductModule {
  return (ALL_MODULES as string[]).includes(value);
}

/**
 * Which modules a tenant has. `enabledModules` comes from tenant config; when
 * it is absent (older API, or config not loaded yet) every module is treated
 * as enabled so nothing is greyed out by mistake.
 */
export function enabledModuleSet(enabledModules: string[] | undefined | null): Set<ProductModule> {
  if (!enabledModules) return new Set(ALL_MODULES);
  return new Set(enabledModules.filter(isProductModule));
}
